import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import styled from "styled-components";

import LoginDialog from "@/components/LoginDialog";
import { colors } from "@/styles/design-system";
import { POST_LOGIN_REDIRECT_KEY } from "@/utils/constants";

/**
 * What an unauthenticated visitor sees where a protected page would be. Renders
 * inside SecondRootLayout, so the header and footer are around it.
 *
 * Only a branded backdrop plus the login modal — the page itself is never
 * rendered (see AuthGuard), so there is nothing behind the modal to reveal and
 * no page data was fetched. The modal's ✕ (and Escape) mean "I'm not signing
 * in" and navigate home rather than closing onto an empty backdrop.
 *
 * `showModal` is false during the auth check so a logged-in-but-still-loading
 * visitor gets a brief branded frame, not a login prompt that then vanishes.
 */
const Backdrop = styled.div`
  min-height: 60vh;
  background:
    radial-gradient(ellipse 60% 50% at 50% 0%, rgba(176, 144, 105, 0.22), transparent 70%),
    linear-gradient(180deg, ${colors.brown.page} 0%, ${colors.brown.dark} 100%);
`;

const LoginGate: FC<{ showModal: boolean }> = ({ showModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authPanel, setAuthPanel] = useState(0);
  const stashed = useRef(false);

  // Remember where they were headed so login returns them here, not the
  // catalog. Once, only when the modal actually shows.
  useEffect(() => {
    if (!showModal || stashed.current) return;
    stashed.current = true;
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, location.pathname + location.search);
    } catch {
      /* storage blocked — login falls back to the catalog */
    }
  }, [showModal, location.pathname, location.search]);

  const leave = () => {
    try {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    } catch {
      /* noop */
    }
    navigate("/");
  };

  return (
    <>
      <Backdrop aria-hidden />
      {showModal && (
        <LoginDialog
          open
          isClosable
          handleClose={leave}
          authPanel={authPanel}
          setAuthPanel={setAuthPanel}
          resetToken={null}
        />
      )}
    </>
  );
};

export default LoginGate;
